<script setup>
import { API_URL } from '@/constants/GlobalConstants.js'
import { buildImageUrlAppendix } from '@/services/helpers/ImageSizeHelper'
import UtilityHelper from '@/services/helpers/UtilityHelper'
import { useDebounceFn } from '@vueuse/core'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { VImg } from 'vuetify/lib/components/index.js'
import { useAccessibilityStore } from '../../../stores/accessibility.js'

const {
  productId,
  campaignId,
  useHeroImage,
  alt,
  isBundle,
  height,
  width,
  observeFetchPriority
} = defineProps({
  height: {
    type: [Number, String],
    default: 300
  },
  width: {
    type: [Number, String],
    default: 300
  },
  productId: [Number, String],
  campaignId: [Number, String],
  useHeroImage: {
    default: false,
    type: Boolean
  },
  alt: String,
  isBundle: {
    default: false,
    type: Boolean
  },
  addItemProp: {
    default: false,
    type: Boolean
  },
  observeFetchPriority: {
    default: false,
    type: Boolean
  }
})

const accessibilityStore = useAccessibilityStore()

const highContrastEnabled = computed(
  () => accessibilityStore.highContrastEnabled
)
const type = computed(() => (useHeroImage ? 'image' : 'small_image'))

const imgHeight = ref(height)
const imgWidth = ref(width)

const imgRef = ref(null)
const vImgRef = ref(null)
let hasMeasured = false

const image = computed(() => {
  if (!productId || !campaignId) return null

  const density = window.devicePixelRatio ?? 1
  const appendix = buildImageUrlAppendix(
    type.value,
    imgHeight.value * density,
    imgWidth.value * density
  )

  return UtilityHelper.formatString(
    API_URL.CATALOG.PRODUCT.GET_V2_IMAGE,
    productId,
    campaignId,
    appendix
  )
})

const placeholderImages = {
  bundle: new URL('@/assets/images/bundle.svg', import.meta.url).href,
  bottle: new URL('@/assets/images/bottle.svg', import.meta.url).href
}

const placeholderImage = computed(() => {
  return placeholderImages[isBundle || useHeroImage ? 'bundle' : 'bottle']
})

const onResize = () => {
  updateImageDimensions()
}

// Measure the VImg box, not the wrapper: the wrapper fills the parent column
// and carries the bundle pt-2 padding, so it overshoots the rendered image.
// The first real measurement replaces the prop-based estimate (CSS can
// constrain the box below the props); afterwards grow-only, since a larger
// cached image scales down fine while shrinking would build a new URL
// (= new download) for an image we already display.
const updateImageDimensions = useDebounceFn(() => {
  const el = vImgRef.value?.$el
  if (!el) return

  // Product images are square-ish, so if one dimension measures 0 (e.g. not
  // yet laid out), the other is a better estimate than keeping it at 0.
  const measuredHeight = el.clientHeight || el.clientWidth
  const measuredWidth = el.clientWidth || el.clientHeight
  if (!measuredHeight && !measuredWidth) return // hidden: keep the estimate

  if (hasMeasured) {
    imgHeight.value = Math.max(Number(imgHeight.value) || 0, measuredHeight)
    imgWidth.value = Math.max(Number(imgWidth.value) || 0, measuredWidth)
  } else {
    imgHeight.value = measuredHeight || imgHeight.value
    imgWidth.value = measuredWidth || imgWidth.value
    hasMeasured = true
  }
}, 300)

// Function to set fetchpriority on img elements
const setFetchPriority = () => {
  if (!imgRef.value) return
  const imgElements = imgRef.value.querySelectorAll('img')
  imgElements.forEach((img) => {
    img.setAttribute('fetchpriority', 'high')
  })
}

let observer = null

onMounted(() => {
  if (!imgRef.value || !observeFetchPriority) return

  setFetchPriority()

  observer = new MutationObserver(() => {
    setFetchPriority()
  })

  observer.observe(imgRef.value, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src']
  })

  watch(image, async () => {
    await nextTick()
    setFetchPriority()
  })
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
    observer = null
  }
})
</script>

<template>
  <div
    ref="imgRef"
    class="product-image position-relative d-flex flex-column align-center justify-center"
    :class="{ 'pt-2': isBundle }"
  >
    <VImg
      ref="vImgRef"
      v-resize="onResize"
      :lazy-src="placeholderImage"
      :src="image"
      :alt="alt"
      :height="height"
      :width="width"
      :class="{ 'high-contrast': highContrastEnabled }"
      :itemprop="addItemProp ? 'image' : null"
      :itemtype="addItemProp ? 'https://schema.org/image' : null"
      :content="addItemProp ? image : ''"
      :options="{ rootMargin: '100%' }"
      fetchpriority="high"
    />
  </div>
</template>

<style scoped>
.product-image {
  max-width: 100%;
}

.high-contrast {
  filter: contrast(1.5);
}
</style>
